(() => {
  const slugify = (value) => value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

  const initArticle = (root) => {
    if (root.dataset.articleReady === 'true') return;
    root.dataset.articleReady = 'true';

    const body = root.querySelector('[data-article-body]');
    const toc = root.querySelector('[data-toc-container]');
    const tocLinks = root.querySelector('[data-toc-links]');
    const progress = root.querySelector('[data-reading-progress]');
    const references = root.querySelector('[data-article-references]');
    const referenceLinks = root.querySelector('[data-reference-links]');
    const resourceLinks = root.querySelector('[data-resource-links]');
    if (!body) return;

    body.querySelectorAll('p').forEach((paragraph) => {
      const hasMeaningfulContent = paragraph.textContent.trim().length > 0
        || paragraph.querySelector('img, picture, video, audio, iframe, table, a[href], button, input, svg');
      if (!hasMeaningfulContent) paragraph.remove();
    });

    body.querySelectorAll('img').forEach((image) => {
      if (!image.hasAttribute('loading')) image.loading = 'lazy';
      image.decoding = 'async';
      if (!image.hasAttribute('alt')) {
        const caption = image.closest('figure')?.querySelector('figcaption')?.textContent.trim();
        image.alt = caption || '';
      }
    });

    body.querySelectorAll('table').forEach((table, index) => {
      let wrapper = table.parentElement;
      if (!wrapper?.classList.contains('ink-article__table-scroll')) {
        wrapper = document.createElement('div');
        wrapper.className = 'ink-article__table-scroll';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', `Scrollable table ${index + 1}`);
        table.before(wrapper);
        wrapper.appendChild(table);
      }

      let tableBlock = wrapper;
      while (tableBlock.parentElement && tableBlock.parentElement !== body) {
        tableBlock = tableBlock.parentElement;
      }
      if (tableBlock.parentElement === body) tableBlock.classList.add('ink-article__table-block');
    });

    const articleLinks = Array.from(body.querySelectorAll('a[href]'));
    const seenResources = new Set(
      Array.from(resourceLinks?.querySelectorAll('a[href]') || []).map((link) => {
        try {
          return new URL(link.href, window.location.href).pathname.replace(/\/$/, '');
        } catch (error) {
          return '';
        }
      })
    );

    if (resourceLinks) {
      articleLinks.forEach((link) => {
        let url;
        try {
          url = new URL(link.href, window.location.href);
        } catch (error) {
          return;
        }

        if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return;
        const key = url.pathname.replace(/\/$/, '');
        if (seenResources.has(key)) return;
        seenResources.add(key);

        const item = document.createElement('li');
        const resource = document.createElement('a');
        const label = document.createElement('span');
        const arrow = document.createElement('span');
        resource.href = url.href;
        label.textContent = link.textContent.trim() || key.split('/').pop().replace(/-/g, ' ');
        arrow.textContent = '→';
        arrow.setAttribute('aria-hidden', 'true');
        resource.append(label, arrow);
        item.appendChild(resource);
        resourceLinks.appendChild(item);
      });

      Array.from(resourceLinks.children)
        .sort((itemA, itemB) => {
          const pathA = new URL(itemA.querySelector('a').href, window.location.href).pathname;
          const pathB = new URL(itemB.querySelector('a').href, window.location.href).pathname;
          return Number(pathB.startsWith('/products/')) - Number(pathA.startsWith('/products/'));
        })
        .forEach((item) => resourceLinks.appendChild(item));
    }

    const seenReferences = new Set();
    articleLinks.forEach((link) => {
      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }

      if (!['http:', 'https:'].includes(url.protocol)) return;
      const external = url.origin !== window.location.origin;
      if (external) link.dataset.externalLink = 'true';
      if (!references || !referenceLinks) return;
      if (url.origin === window.location.origin && url.pathname === window.location.pathname) return;
      if (!external && !url.pathname.startsWith('/blogs/')) return;

      url.hash = '';
      const key = url.href.replace(/\/$/, '');
      if (seenReferences.has(key)) return;
      seenReferences.add(key);

      const label = link.textContent.trim() || url.pathname.split('/').filter(Boolean).pop() || url.hostname;
      let source = url.hostname.replace(/^www\./, '');
      if (!external) source = 'Inkelligent · Article';

      const item = document.createElement('li');
      const reference = document.createElement('a');
      reference.href = url.href;
      reference.textContent = label;
      const sourceLabel = document.createElement('span');
      sourceLabel.className = 'ink-article__reference-source';
      sourceLabel.textContent = source;
      reference.appendChild(sourceLabel);
      item.appendChild(reference);
      referenceLinks.appendChild(item);
    });

    if (references && referenceLinks?.children.length) references.hidden = false;

    const headings = Array.from(body.querySelectorAll('h2'))
      .filter((heading) => heading.textContent.trim().length > 0 && !heading.closest('aside, [data-toc-exclude]'));
    const usedIds = new Set();

    if (toc && tocLinks && headings.length >= 2) {
      const list = document.createElement('ol');
      headings.forEach((heading, index) => {
        let id = heading.id || slugify(heading.textContent) || `section-${index + 1}`;
        const baseId = id;
        let duplicate = 2;
        while (usedIds.has(id) || document.querySelectorAll(`#${CSS.escape(id)}`).length > 1) {
          id = `${baseId}-${duplicate}`;
          duplicate += 1;
        }
        usedIds.add(id);
        heading.id = id;

        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${id}`;
        link.textContent = heading.textContent.trim();
        item.appendChild(link);
        list.appendChild(item);
      });
      tocLinks.appendChild(list);

      if (window.matchMedia('(max-width: 900px)').matches) {
        toc.querySelector('details')?.removeAttribute('open');
      }

      if ('IntersectionObserver' in window) {
        const links = Array.from(tocLinks.querySelectorAll('a'));
        const observer = new IntersectionObserver((entries) => {
          const visible = entries.find((entry) => entry.isIntersecting);
          if (!visible) return;
          links.forEach((link) => link.removeAttribute('aria-current'));
          const active = tocLinks.querySelector(`a[href="#${CSS.escape(visible.target.id)}"]`);
          if (active) active.setAttribute('aria-current', 'location');
        }, { rootMargin: '-18% 0px -72% 0px' });
        headings.forEach((heading) => observer.observe(heading));
      }
    } else if (toc) {
      toc.hidden = true;
    }

    if (progress) {
      let frameRequested = false;
      const updateProgress = () => {
        frameRequested = false;
        const rect = body.getBoundingClientRect();
        const available = Math.max(body.offsetHeight - window.innerHeight, 1);
        const read = Math.min(Math.max(-rect.top, 0), available);
        progress.style.width = `${Math.min((read / available) * 100, 100)}%`;
      };
      const requestProgressUpdate = () => {
        if (frameRequested) return;
        frameRequested = true;
        window.requestAnimationFrame(updateProgress);
      };
      updateProgress();
      document.addEventListener('scroll', requestProgressUpdate, { passive: true });
      window.addEventListener('resize', requestProgressUpdate, { passive: true });
    }
  };

  const init = () => document.querySelectorAll('[data-ink-article]').forEach(initArticle);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
